import { App } from "@/constants";
import { STATUS } from "@/constants/statuses";
import { localizationInfoProviderToken, modalManagerToken } from "@/tokens";
import { reviewsFormModalToken } from "@apps/reviews/tokens";
import { LEAVE_REVIEW_SOURCE } from "@apps/reviews/widgets/review_form_modal_widget/constants";
import { POINTS_SHARE_PATH } from "@apps/rewards/constants/constants";
import RewardsBoostCampaignQuery from "@apps/rewards/modules/BoostCampaign/query/RewardsBoostCampaignQuery";
import RewardsVerificationTokenService from "@apps/rewards/modules/VerificationToken/service/RewardsVerificationTokenService";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { getShareUrl } from "@apps/rewards/utils/getShareUrl";
import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
import {
    getLoadingStatus,
    selectRequestStatus,
} from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import WindowPopupManager from "@lib/WindowPopupManager/WindowPopupManager";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import {
    Observable,
    catchError,
    filter,
    lastValueFrom,
    map,
    take,
    tap,
} from "rxjs";
import { container, inject, singleton } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import type { LoggerInterface } from "@interfaces/LoggerInterface";

import RewardsEarningRulesApiService from "../api_service/RewardsEarningRulesApiService";
import {
    REWARDS_BUY_X_TO_GET_Y_OBJECT_TYPES,
    REWARDS_EARNING_RULE_TYPES,
} from "../constants";
import type {
    RewardsEarningRulesEntity,
    RewardsEarningRulesResponse,
} from "../models/RewardsEarningRulesEntities";
import RewardsEarningRulesQuery from "../query/RewardsEarningRulesQuery";
import RewardsEarningRulesStore from "../store/RewardsEarningRulesStore";
import { REWARDS_EARNING_RULE_HANDLERS_TYPE } from "../types";

@singleton()
class RewardsEarningRulesService {
    private readonly earningRulesRequestStatus$: Observable<STATUS>;
    private readonly boostCampaignRequestStatus$: Observable<STATUS>;
    private readonly locale: string;
    private modalManager: ModalManager | null = null;

    public readonly earningRulesList$: Observable<RewardsEarningRulesEntity[]>;

    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        @inject(localizationInfoProviderToken)
        private readonly localizationInfoProvider: LocalizationInfoProviderInterface,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        private readonly earningRulesApiService: RewardsEarningRulesApiService,
        private readonly earningRulesStore: RewardsEarningRulesStore,
        private readonly earningRulesQuery: RewardsEarningRulesQuery,
        private readonly boostCampaignQuery: RewardsBoostCampaignQuery,
        private readonly verificationTokenService: RewardsVerificationTokenService,
        private readonly baseModule: BaseModule,
        private readonly tokenManagerModule: TokenManagerModule,
        private readonly windowPopupManager: WindowPopupManager
    ) {
        this.earningRulesList$ = this.earningRulesQuery.earningRulesList$;
        this.earningRulesRequestStatus$ = this.earningRulesQuery.status$;
        this.boostCampaignRequestStatus$ = this.boostCampaignQuery.status$;

        this.locale = normalizeLocale(
            this.localizationInfoProvider.getLanguageIsoCode()
        );

        this.baseModule.exports.modulesStatusesProvider
            .selectModuleStatus(App.Reviews)
            .pipe(
                filter((status) => status === MODULE_STATUS.INITIALIZED),
                tap(
                    () =>
                        (this.modalManager =
                            container.resolve(modalManagerToken))
                ),
                take(1)
            )
            .subscribe();
    }

    //Logic
    public getSortedRulesList(): Observable<RewardsEarningRulesEntity[]> {
        return this.earningRulesQuery.earningRulesList$.pipe(
            map((rulesList) =>
                [...rulesList].sort(
                    (a, b) => Number(a.completed) - Number(b.completed)
                )
            )
        );
    }

    public isInitialLoading(): Observable<boolean> {
        return selectRequestStatus(
            [this.earningRulesRequestStatus$, this.boostCampaignRequestStatus$],
            STATUS.INITIAL_LOADING
        );
    }

    public getHandlersMap(): REWARDS_EARNING_RULE_HANDLERS_TYPE {
        const handlersMap = <REWARDS_EARNING_RULE_HANDLERS_TYPE>{};

        Object.values(REWARDS_EARNING_RULE_TYPES).forEach((type) => {
            switch (type) {
                case REWARDS_EARNING_RULE_TYPES.placeOrder:
                case REWARDS_EARNING_RULE_TYPES.goalSpend:
                case REWARDS_EARNING_RULE_TYPES.punchCard:
                    handlersMap[type] = () =>
                        this.handleRedirect(
                            this.commonModule.exports.urlsBuilder.createCollectionAllUrl()
                        );
                    break;

                case REWARDS_EARNING_RULE_TYPES.buyXToGetY:
                    handlersMap[type] = (rule: RewardsEarningRulesEntity) =>
                        this.handleRedirect(this.getBuyXToGetYLink(rule));
                    break;

                case REWARDS_EARNING_RULE_TYPES.instagramFollow:
                case REWARDS_EARNING_RULE_TYPES.twitterFollow:
                case REWARDS_EARNING_RULE_TYPES.twitterShare:
                case REWARDS_EARNING_RULE_TYPES.facebookShare:
                case REWARDS_EARNING_RULE_TYPES.facebookPageVisit:
                case REWARDS_EARNING_RULE_TYPES.followOnTikTok:
                case REWARDS_EARNING_RULE_TYPES.followOnYouTube:
                case REWARDS_EARNING_RULE_TYPES.followOnCustom:
                    handlersMap[type] = (rule: RewardsEarningRulesEntity) =>
                        this.handleOpenPopup(rule);
                    break;
                case REWARDS_EARNING_RULE_TYPES.leaveReview:
                    handlersMap[type] = () => this.handleLeaveReview();
                    break;
                case REWARDS_EARNING_RULE_TYPES.createAccount:
                    handlersMap[type] = () => this.handleVerifyAccount();
                    break;

                default:
                    handlersMap[type] = null;
            }
        });

        return handlersMap;
    }

    private getBuyXToGetYLink(rule: RewardsEarningRulesEntity): string {
        if (rule.type !== REWARDS_EARNING_RULE_TYPES.buyXToGetY) return "";

        return rule.objectType === REWARDS_BUY_X_TO_GET_Y_OBJECT_TYPES.product
            ? this.commonModule.exports.urlsBuilder.createProductUrl(
                  rule.objectHandle
              )
            : this.commonModule.exports.urlsBuilder.createCollectionUrl(
                  rule.objectHandle
              );
    }

    //Handlers
    public async handleVerifyAccount(): Promise<void> {
        await lastValueFrom(
            this.verificationTokenService.sendVerificationToken()
        );
    }

    private handleRedirect(link: string | undefined): void {
        if (!link) return;

        this.commonModule.exports.locationController.goTo(link);
    }

    private handleOpenPopup = (rule: RewardsEarningRulesEntity) => {
        this.windowPopupManager.open(
            getShareUrl(
                POINTS_SHARE_PATH,
                {
                    ruleId: rule.id,
                    eventType: rule.type,
                },
                this.tokenManagerModule.exports.tokenManager
            )
        );

        this.windowPopupManager
            .selectClosedPopup()
            .subscribe(() => void lastValueFrom(this.getEarningRules()));
    };

    private handleLeaveReview(): void {
        if (!this.modalManager) return;

        this.modalManager.openModal(reviewsFormModalToken, undefined, {
            source: LEAVE_REVIEW_SOURCE.REWARDS_WAYS_TO_EARN_PAGE_WIDGET,
            showPurchasedProducts: true,
        });
    }

    //Api calls
    public getEarningRules(): Observable<RewardsEarningRulesResponse> {
        this.logger.debug(
            "RewardsEarningRulesService.getEarningRules is started"
        );

        const status = this.earningRulesStore.getStatus();
        this.earningRulesStore.setStatus(getLoadingStatus(status));

        return this.earningRulesApiService
            .getEarningRules({ locale: this.locale })
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsEarningRulesService.getEarningRules is errored",
                        error
                    );
                    this.earningRulesStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsEarningRulesService.getEarningRules is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.earningRulesStore.update((state) => ({
                            ...state,
                            earningRulesList: value.rules,
                        }));
                        this.earningRulesStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetEarningRules(): Promise<void> {
        if (this.earningRulesStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getEarningRules());
    }
}

export default RewardsEarningRulesService;
